import PropTypes from 'prop-types';
import React from 'react';
import { LinkButton } from '@strava/ui/Button';
import { Markup } from 'interweave';
import I18n from 'utils/I18n';
import { useFeedEntityContext } from '../FeedEntityContext';
import {
  indexProps,
  cursorDataProps,
  viewingAthleteProps,
  timeAndLocationProps
} from '../../../props/feed-prop-types';
import HeaderWithOwnerMetadata from '../HeaderWithOwnerMetadata/HeaderWithOwnerMetadata';
import styles from './index.scss';

import Card from '../media/Card';
import EntryBody from '../media/EntryBody';
import { FEED_ENTRY_TYPES } from '../../../constants';

const PerformanceGoalSingleView = ({
  entity,
  entityId,
  intersectionObserverRef,
  cursorData,
  index,
  viewingAthlete,
  shareable,
  twitterUrl,
  isBoosted,
  timeAndLocation,
  goalEntries
}) => {
  const { feedEntityState } = useFeedEntityContext();

  const goalEntry = goalEntries[0];
  const joinable = viewingAthlete.id !== goalEntry.athlete_id.toString(); // Cast to string to ensure that both are strings
  const entityOwner = {
    displayImage: goalEntry.athlete_avatar_url,
    name: goalEntry.athlete_display_name,
    memberType: goalEntry.athlete_member_type,
    profileUrl: `/athletes/${goalEntry.athlete_id}`,
    type: 'athlete'
  };

  return (
    <Card
      key={index}
      index={index}
      cursorData={cursorData}
      ref={intersectionObserverRef}
    >
      <HeaderWithOwnerMetadata
        entityOwner={entityOwner}
        entityId={`${entityId}`}
        twitterUrl={twitterUrl}
        isBoosted={isBoosted}
        shareable={shareable}
        timeAndLocation={timeAndLocation}
        embedDropdownEnabled={false}
        entityParentId={goalEntry.athlete_id}
        entityParentType={FEED_ENTRY_TYPES.performanceGoalCreated}
      />
      <EntryBody id={goalEntry.athlete_feed_entry_id}>
        <h3 className={styles.entryTitle} data-testid="goal-description">
          <Markup noWrap={true} content={goalEntry.description} />
        </h3>
        {joinable && (
          <LinkButton
            variant="default"
            className={styles.ctaBtn}
            href={goalEntry.goal_path}
            data-testid="cta-btn"
            onClick={() => {
              feedEntityState.trackAnalytics({
                action: 'click',
                element: `${entity.toLowerCase()}_join_goal`
              });
            }}
          >
            {I18n.t(
              'components.feed.performance_goal_created_single_view.join_goal'
            )}
          </LinkButton>
        )}
      </EntryBody>
    </Card>
  );
};

PerformanceGoalSingleView.propTypes = {
  entity: PropTypes.oneOf(['PerformanceGoalCreated']).isRequired,
  entityId: PropTypes.number.isRequired,
  intersectionObserverRef: PropTypes.func.isRequired,
  cursorData: cursorDataProps.isRequired,
  index: indexProps.isRequired,
  viewingAthlete: viewingAthleteProps.isRequired,
  shareable: PropTypes.bool,
  twitterUrl: PropTypes.string.isRequired,
  isBoosted: PropTypes.bool,
  timeAndLocation: timeAndLocationProps.isRequired,
  goalEntries: PropTypes.arrayOf(
    PropTypes.shape({
      shareable: PropTypes.bool,
      is_boosted: PropTypes.bool,
      twitter_url: PropTypes.string.isRequired,
      entity_id: PropTypes.number.isRequired,
      description: PropTypes.string.isRequired,
      athlete_feed_entry_id: PropTypes.number.isRequired,
      athlete_id: PropTypes.number.isRequired,
      goal_path: PropTypes.string.isRequired,
      athlete_avatar_url: PropTypes.string.isRequired,
      athlete_member_type: PropTypes.string.isRequired,
      athlete_display_name: PropTypes.string.isRequired
    })
  ).isRequired
};

PerformanceGoalSingleView.defaultProps = {
  isBoosted: false,
  shareable: false
};

export default PerformanceGoalSingleView;
